import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Menu } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Logo } from '@/components/logo'
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { ThemeToggle } from '@/components/theme-toggle'

interface HeaderProps {
  className?: string
}

export function Header({ className }: HeaderProps) {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()

  const navigationItems = [
    { name: 'Features', href: '#features' },
    { name: 'Pricing', href: '#pricing' },
    { name: 'Roadmap', href: '/roadmap' },
    { name: 'Documentation', href: 'https://docs.ecosemantic.com', external: true },
  ]

  const handleLogin = () => {
    navigate('/auth?mode=login')
  }

  const handleSignup = () => {
    navigate('/auth?mode=signup')
  }

  const handleNavClick = (href: string, external?: boolean) => {
    setOpen(false)

    // Handle external links
    if (external) {
      window.open(href, '_blank', 'noopener,noreferrer')
      return
    }

    // Handle internal routes (like /roadmap)
    if (href.startsWith('/') && !href.startsWith('/#')) {
      navigate(href)
      return
    }

    // If it's a hash link
    if (href.startsWith('#')) {
      const currentPath = window.location.pathname

      // If we're on the landing page, just scroll
      if (currentPath === '/') {
        setTimeout(() => {
          const element = document.querySelector(href)
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' })
          }
        }, 100)
      } else {
        // If we're on a different page, do a full navigation
        window.location.href = '/' + href
      }
    }
  }

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 ${className}`}
    >
      <div className="container mx-auto max-w-7xl flex h-16 items-center justify-between px-6">
        {/* Logo */}
        <button
          onClick={() => (window.location.href = '/')}
          className="flex items-center space-x-3 cursor-pointer"
        >
          <Logo size={32} showText={true} />
        </button>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-6 text-sm font-medium">
          {navigationItems.map((item) =>
            item.external ? (
              <a
                key={item.name}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                className="transition-colors hover:text-foreground/80 text-foreground/60"
              >
                {item.name}
              </a>
            ) : (
              <button
                key={item.name}
                onClick={() => handleNavClick(item.href)}
                className="transition-colors hover:text-foreground/80 text-foreground/60"
              >
                {item.name}
              </button>
            ),
          )}
        </nav>

        {/* Desktop Actions */}
        <div className="hidden md:flex items-center space-x-4">
          <ThemeToggle />
          <Button variant="ghost" size="sm" onClick={handleLogin}>
            Login
          </Button>
          <Button size="sm" onClick={handleSignup}>
            Sign Up
          </Button>
        </div>

        {/* Mobile Menu */}
        <div className="flex md:hidden items-center space-x-2">
          <ThemeToggle />
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon">
                <Menu className="h-5 w-5" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[400px]">
              <SheetTitle className="sr-only">Mobile Navigation Menu</SheetTitle>
              <SheetDescription className="sr-only">
                Navigation menu for EcoSemantic website
              </SheetDescription>
              <div className="flex flex-col space-y-6 mt-8 px-2">
                <div className="flex items-center space-x-2 pb-6 border-b">
                  <Logo size={32} showText={true} />
                </div>

                <nav className="flex flex-col space-y-4">
                  {navigationItems.map((item) => (
                    <button
                      key={item.name}
                      onClick={() => handleNavClick(item.href, item.external)}
                      className="text-left px-4 py-3 text-lg font-medium transition-colors hover:text-foreground/80 text-foreground/60 rounded-lg hover:bg-muted"
                    >
                      {item.name}
                    </button>
                  ))}
                </nav>

                <div className="pt-6 border-t space-y-4">
                  <Button variant="outline" size="lg" className="w-full" onClick={handleLogin}>
                    Login
                  </Button>
                  <Button size="lg" className="w-full" onClick={handleSignup}>
                    Sign Up
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  )
}
